/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	Box,
	Button,
	CardActions,
	CardContent,
	Typography,
	FormHelperText,
	CircularProgress,
	Container,
	Stack,
	Grid,
	useTheme,
} from '@mui/material';
import '@/styles/fileuploader.scss';
import { Formik, Form, useFormikContext } from 'formik';
import {
	folderStructure,
	getPipelineIcon,
	replaceFolderName,
	splitName,
	tryDecodeFilePath,
} from '@/utils/constants';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useDispatch, useSelector } from 'react-redux';
import {
	useGetClientRowQuery,
	useUpdateClientxMutation,
	useGetClientPropertiesQuery,
	useLazyGetClientPropertiesQuery,
} from '@/features/clients/clientsApi';
import { useCreateActivityMutation } from '@/features/engagements/engagementsApi';
import newBusinessValidationSchema, {
	ADDRESS,
	CITY,
	CLIENT_ID,
	CLIENT_NAME,
	COUNTRY,
	EMAIL,
	FILES,
	FIRST_NAME,
	INDUSTRY,
	initialValues as initValues,
	LAST_NAME,
	OTHER_INFO,
	PHONE,
	PHONE2,
	DATE_OF_BIRTH,
	WEBSITE,
	OWNER_DOC_ID,
	COMPLETED_BY,
	SUBURB,
	POSTCODE,
	CLIENT_CATEGORY,
	SCOPE_OF_ADVICE,
	CLIENT_TYPE,
	COMMERCIAL,
	ANNUAL_TURNOVER,
	NO_EMPLOYEES,
	YEAR_FOLDER,
	DEAL_OVERVIEW,
	CONTACTS,
	SOURCE_OF_BUSINESS,
	ANZSIC_CODE,
	SALESTEAM,
	DEAL_AMOUNT,
	GOALS_OF_COVER_LABELS,
	REFERRAL_PARTNER,
	BROKER_GROUP_ID,
} from './validationSchema';
import {
	default as folderValidationSchema,
	FOLDER_NAME,
} from '@/components/dialogs/AddFolderSharepointDialog/validationSchema';
import { useClientStorageHooks } from '@/hooks/useClientStorageHooks';
import { PageHeader } from '@/components/layouts/PageHeader';
import { showToast } from '@/features/toast/toastSlice';
import { useLocation } from 'react-router-dom';
import { FormikErrorScroll } from '@/hooks/useFormikHelper';
import FileUpload from '@/components/FileUpload';
import { AdditionalScopeFields } from './Fields/AdditionalScopeFields';
import { BusinessDescription } from './Fields/BusinessDescription';
import { SearchClient } from './Fields/SearchClient';
import { paths } from '@/app/routes';
import { useCreateNewBusinessMutation } from '@/features/deals/dealsApi';
import { UPLOAD_FILES_HELP_TEXT } from '@/utils/helperText';
import {
	useAddFileToSharepointMutation,
	useLazySearchFoldersAndFilesQuery,
} from '@/features/msGraph/msGraphApi';
import DealNaming from '../fields/DealNaming';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { useListContactsQuery } from '@/features/contacts/contactsApi';
import { generateBusinessFormPDF } from '@/utils/generateBusinessFormPDF';
import FormNavigationBlocker from '../FormNavigationBlocker';
import { useLazyGetInsightObjectByIdQuery } from '@/features/properties/propertiesApi';
import { useLazyGetClassesOfRiskQuery } from '@/features/classesOfRisk/classesOfRiskApi';
import DealFormActions from '@/components/forms/CreateFormFields/DealFormActions';
import DealFormMessages from '@/components/forms/CreateFormFields/DealFormMessages';
import RenewalDateField from '@/components/forms/CreateFormFields/RenewalDateField';
import AdviserFeeField from '@/components/forms/CreateFormFields/AdviserFeeField';
import { extractMidnightUTCTimestamp } from '@/utils/date';

const CreateNewBusinessForm = () => {
	const dispatch = useDispatch();
	const location = useLocation();

	const isSubmittingRef = useRef(false);

	const existingClientId =
		location?.state?.clientId ??
		location?.state?.client?.clientId ??
		location?.state?.client?.id;

	const { currentData: existingClient } = useGetClientPropertiesQuery(existingClientId, {
		skip: !existingClientId,
	});

	const [addFileToSharepoint] = useAddFileToSharepointMutation();
	const [searchFoldersAndFiles] = useLazySearchFoldersAndFilesQuery();

	const existingContact = location.state?.contact ?? {};

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const [formLoading, setFormLoading] = useState(false);

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const brokerGroupIds = userDetails?.brokerGroupArray;

	const [brokerGroupId, setBrokerGroupId] = useState(userDetails?.brokerGroupId);

	useEffect(() => {
		if (!brokerGroupId && userDetails?.brokerGroupId)
			setBrokerGroupId(userDetails?.brokerGroupId);
	}, [brokerGroupId, userDetails?.brokerGroupId]);

	const [updateClientx] = useUpdateClientxMutation();
	const [createActivity] = useCreateActivityMutation();

	const [getClientProperties] = useLazyGetClientPropertiesQuery();
	const [getClassesOfRisk] = useLazyGetClassesOfRiskQuery();

	const { getFeatureFlags } = useFeatureFlagContext();

	const isClassOfRiskRequired = Boolean(getFeatureFlags('riskInfoRequired')?.active);
	const isDealAmountRequired = Boolean(getFeatureFlags('dealAmountRequired')?.active);

	const [createNewBusiness, { isDealLoading }] = useCreateNewBusinessMutation();

	const createNewClient = async (values) => {
		// If the existing client is a prospect, convert it to a client
		if (existingClient?.clientRow?.isProspect) {
			await updateClientx({
				clientId: existingClient?.clientRow?.clientId,
				insight: {
					ClientTypeId: 0,
					ClientType: 0,
				},
				firebase: {},
			}).unwrap();
			await createActivity({
				clientId: existingClient?.clientRow?.id,
				activityType: 'prospectToClient',
			}).unwrap();
		}

		const body = {
			clientId: values.clientId,
			ownerDocId: values[OWNER_DOC_ID],
			dealName: values[FOLDER_NAME].trim(),
			dealOverview: (values[DEAL_OVERVIEW] ?? '').trim(),
			notes: values.otherInfo,
			adviserFee: values.adviserFee,
			clientGoals: values.goalsOfCover,
			additionalGoals: values.additionalGoalsOfCover,
			clientCategory: values.clientCategory,
			clientType: values.clientType,
			needsAnalysis: values.needsAnalysis,
			additionalProducts: values.additionalSpecialistProducts,
			riskClasses: values.scopeOfAdvice,
			yearFolder: values[YEAR_FOLDER],
			dealAmount: values[DEAL_AMOUNT],
			...(values.renewalDate && {
				renewalDate: extractMidnightUTCTimestamp(values.renewalDate),
			}),
			...(values.estimatedCloseDate && {
				estimatedCloseDate: extractMidnightUTCTimestamp(values.estimatedCloseDate),
			}),
			...(values[CLIENT_CATEGORY].length > 0 && {
				[CLIENT_CATEGORY]: values[CLIENT_CATEGORY],
			}),
			...(values[SCOPE_OF_ADVICE].length > 0 && {
				[SCOPE_OF_ADVICE]: values[SCOPE_OF_ADVICE].map((v) =>
					typeof v === 'string' ? v : v.classOfRisk,
				),
			}),
		};

		return await createNewBusiness(body)
			.unwrap()
			.catch((err) => {
				console.log('🙅 ~ Error creating new business', err);
				return { error: { message: 'Error creating new business deal' } };
			});
	};

	const initialValues = useMemo(() => {
		return {
			...initValues,
			[BROKER_GROUP_ID]: userDetails?.brokerGroupId,
			[CLIENT_TYPE]: COMMERCIAL,
			[OWNER_DOC_ID]: userDetails?.userId,
			[SALESTEAM]: userDetails?.userId,
			...(existingClient?.clientRow?.clientId && {
				[INDUSTRY]: existingClient.clientRow?.industry ?? '',
				[BROKER_GROUP_ID]:
					existingClient.clientRow?.brokerGroupId ?? userDetails?.brokerGroupId,
				[ANZSIC_CODE]: existingClient.clientRow?.standardIndustryCode,
				[SOURCE_OF_BUSINESS]: existingClient.insight?.SourceOfBusinessId ?? null,
				[NO_EMPLOYEES]: existingClient.clientRow?.numberOfEmployees ?? 0,
				[WEBSITE]: existingClient.website ?? '',
				[ANNUAL_TURNOVER]: existingClient.clientRow?.annualRevenue ?? 0,
				[CLIENT_NAME]: existingClient.name,
				[OTHER_INFO]: existingClient.clientRow?.description ?? '',
				[CLIENT_ID]: existingClient.clientRow?.clientId,
				[ADDRESS]: existingClient.insight?.AddressLine1 ?? '',
				[POSTCODE]: existingClient.insight?.Postcode ?? '',
				[COUNTRY]: existingClient.insight?.CountryName ?? '',
				[SUBURB]: existingClient.insight?.Suburb ?? '',
				[CITY]: existingClient.insight?.City ?? '',
				[REFERRAL_PARTNER]: existingClient.clientRow?.referralPartner,
			}),
			//Object.keys(existingClient).length === 0 &&
			...(Object.keys(existingContact).length > 0 && {
				[CONTACTS]: [
					{
						[FIRST_NAME]: existingContact.FirstName ?? '',
						[LAST_NAME]: existingContact.LastName ?? '',
						[PHONE]: existingContact.PhoneNumber ?? '',
						[PHONE2]: existingContact.PhoneNumber2 ?? '',
						[DATE_OF_BIRTH]: existingContact.BirthDate ?? '',
						[EMAIL]: existingContact.Email ?? '',
						docId: existingContact.docId,
					},
				],
			}),
		};
	}, [existingClient, existingContact, userDetails?.brokerGroupId]);

	const [getInsightObjectById] = useLazyGetInsightObjectByIdQuery();
	// Helper function for source of business
	const getSourceOfBusinessLabel = async (sourceValue) => {
		if (!sourceValue) return '';
		try {
			const source = await getInsightObjectById(
				{ objectType: 'SourceOfBusiness', id: sourceValue },
				true,
			).unwrap();
			return source?.Name || '';
		} catch (e) {
			console.error('Error getting source of business', e);
			return '';
		}
	};
	// Helper function for industry
	const getIndustryLabel = async (industryCode) => {
		if (!industryCode) return '';
		try {
			const industry = await getInsightObjectById(
				{ objectType: 'IndustryType', id: industryCode },
				true,
			).unwrap();
			return industry?.Name || industryCode;
		} catch (e) {
			console.error('Error getting industry', e);
			return industryCode;
		}
	};

	const generateAndUploadPdf = async ({ values, dealId, clientId }) => {
		try {
			// get the client properties based on the clientId
			const clientProperties = await getClientProperties(clientId).unwrap();

			const insight = clientProperties?.insight;
			const clientRow = clientProperties?.clientRow;

			const assignedToName =
				salesTeamUserDetails?.name ||
				salesTeamUserDetails?.firstName + ' ' + salesTeamUserDetails?.lastName ||
				values[OWNER_DOC_ID];

			const completedBy = userDetails?.name;

			const classesOfRisk = await getClassesOfRisk({
				clientType: values[CLIENT_TYPE],
				countryCode: userDetails?.broker?.country ?? 'NZ',
				brokerGroupIds,
			}).unwrap();

			console.log('[generateAndUploadPdf] Classes of risk', classesOfRisk);

			let scopeOfAdvice;
			try {
				scopeOfAdvice = (values[SCOPE_OF_ADVICE] ?? []).map((risk) => {
					return typeof risk === 'string'
						? Array.isArray(classesOfRisk)
							? (classesOfRisk?.find((r) => r.classOfRisk === risk)?.label ?? risk)
							: risk
						: risk.label;
				});
			} catch (e) {
				console.error('Error getting parsed scope of advice from classes of risk', e);
			}

			const industryLabel = await getIndustryLabel(clientRow?.industry || values[INDUSTRY]);
			const sourceOfBusinessLabel = await getSourceOfBusinessLabel(
				insight?.SourceOfBusinessId || values[SOURCE_OF_BUSINESS],
			);

			const pdfGenerationObject = {
				...values,
				[OWNER_DOC_ID]: assignedToName,
				[COMPLETED_BY]: completedBy,
				// Address fields
				[ADDRESS]: insight?.AddressLine1 || values[ADDRESS],
				[SUBURB]: insight?.Suburb || values[SUBURB],
				[POSTCODE]: insight?.Postcode || values[POSTCODE],
				[CITY]: insight?.City || values[CITY],
				[COUNTRY]: insight?.CountryName || values[COUNTRY],
				// Client details
				[CLIENT_CATEGORY]: clientRow?.clientCategory || values[CLIENT_CATEGORY],
				[SCOPE_OF_ADVICE]: values[SCOPE_OF_ADVICE],
				[WEBSITE]: clientRow?.website || values[WEBSITE],
				[INDUSTRY]: industryLabel,
				[ANZSIC_CODE]: clientRow?.standardIndustryCode || values[ANZSIC_CODE],
				[NO_EMPLOYEES]: clientRow?.numberOfEmployees || values[NO_EMPLOYEES],
				[ANNUAL_TURNOVER]: clientRow?.annualRevenue || values[ANNUAL_TURNOVER],
				[SOURCE_OF_BUSINESS]: sourceOfBusinessLabel,
				// Contact details
				[FIRST_NAME]: clientRow?.contactFirstName || values[FIRST_NAME],
				[LAST_NAME]: clientRow?.contactLastName || values[LAST_NAME],
				[PHONE]: clientRow?.contactPhone || values[PHONE],
				[EMAIL]: clientRow?.contactEmail || values[EMAIL],
				[SCOPE_OF_ADVICE]: scopeOfAdvice ?? values[SCOPE_OF_ADVICE] ?? [],
				goalsOfCover: (values.goalsOfCover ?? []).map(
					(goal) => GOALS_OF_COVER_LABELS[goal],
				),
			};

			// pass these properties merged with the formik.values to the generateBusinessFormPDF function
			const pdfDoc = generateBusinessFormPDF(pdfGenerationObject);

			const pdfBlob = pdfDoc.output('blob');

			const fileName = `${
				values[FOLDER_NAME] || replaceFolderName(values.clientName)
			}_New_Business_Application_${new Date().toISOString().split('T')[0]}.pdf`;
			const pdfFile = new File([pdfBlob], fileName, {
				type: 'application/pdf',
				lastModified: Date.now(),
			});

			if (clientId) {
				const newVals = dealId
					? await getDealDriveId(dealId)
					: await getClientDriveId(clientId);

				console.log('new values: ', newVals);
				//ClientRow doesn't have the notesDriveId property so search for it...
				let notesDriveId = dealId
					? newVals?.dealRow?.notesDriveId
					: newVals?.clientRow?.notesDriveId;
				const folderPath = dealId ? folderStructure.notes : folderStructure.clientNotes;
				if (notesDriveId == null) {
					try {
						if (newVals.driveId && newVals.clientFolderDriveId) {
							const { folders: drives } = await searchFoldersAndFiles({
								itemId: newVals.driveId,
								driveId: newVals.clientFolderDriveId,
								searchText: decodeURI(folderPath.normalize()),
								exact: true,
							}).unwrap();
							console.log('[handleCreateNote] searchFoldersAndFiles FOLDERS', drives);
							const drive = drives?.find((d) =>
								tryDecodeFilePath(d.name, folderPath),
							);
							notesDriveId = drive?.id;
						}
					} catch (e) {
						console.error('Error getting note path', e);
					}
				}

				await addFileToSharepoint({
					driveId: dealId ? newVals?.clientFolderDriveId : newVals?.clientFolderDriveId,
					itemId: notesDriveId,
					file: pdfFile,
				}).unwrap();
			}

			return true;
		} catch (pdfError) {
			console.error('generateAndUploadPdf | error', pdfError);

			throw pdfError;
		}
	};

	const createNewBusinessFolders = async ({ values, clientId, dealId, noteId }) => {
		try {
			const clientProperties = await getClientProperties(clientId).unwrap();
			const clientRow = clientProperties?.clientRow;
			await setUpDealFilesAndFolders({
				dealId,
				noteId,
				clientId,
				clientName: clientRow.name,
				dealName: values[FOLDER_NAME],
				pipelineId: process.env.REACT_APP_PIPELINE_NEW_BUSINESS,
				files: values.files,
				yearFolder: values.yearFolder,
			});
			return { success: true };
		} catch (error) {
			console.error('createNewBusinessFolders | error', error);
			return { error: { message: 'Error creating new business folders' } };
		}
	};

	const [schema, setSchema] = useState(
		newBusinessValidationSchema(isClassOfRiskRequired, isDealAmountRequired),
	);

	const [salesTeam, setSalesTeam] = useState();

	//get sales team data this is so I can pass this to the setUpDealsFolders etc... //Created a seperate get user details call as it would impact featureflags etc
	const { data: salesTeamUserDetails } = useGetUserDetailsQuery(
		{ userId: salesTeam },
		{ skip: !salesTeam },
	);

	const { setUpDealFilesAndFolders, getClientDriveId, getDealDriveId } =
		useClientStorageHooks(brokerGroupId);

	return (
		<Formik
			{...{
				initialValues,
				validateOnChange: false,
				validationSchema: schema,
				enableReinitialize: true,
				onSubmit: async (values, { resetForm, setFieldValue, setSubmitting }) => {
					console.log('📋 ~ Formik values', values);
					if (isSubmittingRef.current) return;

					isSubmittingRef.current = true;
					setFormLoading(true);

					const resetSubmittingState = () => {
						setSubmitting(false);
						setFormLoading(false);
						isSubmittingRef.current = false;
					};
					const createError = (message) => {
						setFieldValue('errorMessage', message ?? 'Error creating new business');
						setFieldValue('successMessage', '');
						resetSubmittingState();
					};

					try {
						const res = await createNewClient(values);

						if (res?.error) {
							console.log('🙅 ~ Error creating new business', res?.error);
							createError(res.error.message);
							dispatch(
								showToast({
									message: res.error.message,
									error: true,
								}),
							);
							return;
						}

						const response = await createNewBusinessFolders({
							values,
							clientId: res.companyId,
							dealId: res.dealId,
							noteId: res.noteId,
						});
						if (response?.error) {
							createError(response?.error?.message);

							dispatch(
								showToast({
									message: `New Business created! ${response?.error?.message}`,
									action: {
										path: `${paths.newBusiness}/${res.dealId}`,
										label: 'GO TO NEW BUSINESS',
									},
									autohide: false,
								}),
							);
							return;
						} else if (res?.companyId) {
							try {
								await generateAndUploadPdf({
									values,
									dealId: res?.dealId,
									clientId: res.companyId,
								});
							} catch (pdfError) {
								console.error(
									'CreateNewBusinessFolders | pdf generation failed, but business was created',
									pdfError,
								);
							}
						}

						dispatch(
							showToast({
								message: 'New Business created!',
								action: {
									path: `${paths.newBusiness}/${res.dealId}`,
									label: 'GO TO NEW BUSINESS',
								},
								autohide: false,
							}),
						);

						console.log('🚀 ~ Successfully created new business', res);
						window.scrollTo(0, 0);
						window.history.replaceState({}, '');
						resetForm();
						setFieldValue('successMessage', 'Successfully created New Business');
						setFieldValue('errorMessage', '');
					} finally {
						resetSubmittingState();
					}
				},
			}}
		>
			{({ isSubmitting }) => {
				const loading = isSubmitting || formLoading || isDealLoading;

				return (
					<CreateNewBusinessFormBody
						{...{ loading, setSchema, setSalesTeam, setBrokerGroupId }}
					/>
				);
			}}
		</Formik>
	);
};

export default CreateNewBusinessForm;

const CreateNewBusinessFormBody = ({ loading, setSchema, setSalesTeam, setBrokerGroupId }) => {
	const theme = useTheme();
	const location = useLocation();

	const existingClientId = location?.state?.clientId ?? location?.state?.client?.clientId;

	const { currentData: existingClient } = useGetClientPropertiesQuery(existingClientId, {
		skip: !existingClientId,
	});

	const isExistingClient = useMemo(
		() => Object.keys(existingClient ?? {}).length > 0,
		[existingClient],
	);

	const { values, isSubmitting, setFieldValue, errors, touched } = useFormikContext();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const {
		data: userDetails,
		isLoading,
		isUninitialized,
	} = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);
	const external = userDetails?.external;

	const canCreate = useMemo(
		() =>
			external
				? userDetails?.salesTeamId != null
				: userDetails?.salesTeamId != null && userDetails?.authorisedRepId != null,
		[userDetails],
	);

	const { getFeatureFlags } = useFeatureFlagContext();

	const isClassOfRiskRequired = Boolean(getFeatureFlags('riskInfoRequired')?.active);
	const isDealAmountRequired = Boolean(getFeatureFlags('dealAmountRequired')?.active);

	const { data: clientRow } = useGetClientRowQuery(values.clientId, {
		skip: !values.clientId,
	});

	useEffect(() => {
		if (!values.otherInfo && clientRow?.description) {
			setFieldValue(OTHER_INFO, clientRow?.description);
		}
	}, [clientRow?.description]);

	const { data: contacts, ...contactQuery } = useListContactsQuery(values[CLIENT_ID], {
		skip: !isExistingClient,
		selectFromResult: ({ data, ...rest }) => {
			const contacts = data ?? [];
			return {
				...rest,
				data: contacts,
			};
		},
	});

	useEffect(() => {
		if (contacts.length > 0) {
			setFieldValue(
				CONTACTS,
				contacts.map((c) => {
					const { firstName, lastName } = splitName(c.Name ?? c.Addressee);
					return {
						[FIRST_NAME]: firstName ?? '',
						[LAST_NAME]: lastName ?? '',
						[PHONE]: c.Telephone ?? '',
						[PHONE2]: c.Telephone2 ?? '',
						[DATE_OF_BIRTH]: c.BirthDate ?? '',
						[EMAIL]: c.Email ?? '',
						Id: c.Id,
					};
				}),
			);
		}
	}, [contacts]);

	const { data: clientProps } = useGetClientPropertiesQuery(values[CLIENT_ID], {
		skip: !values[CLIENT_ID],
	});

	const { data: salesTeamUserDetails } = useGetUserDetailsQuery(
		{ userId: values[SALESTEAM] },
		{ skip: !values[SALESTEAM] },
	);

	useEffect(() => {
		const nextBrokerGroupId =
			clientProps?.clientRow?.brokerGroupId ?? salesTeamUserDetails?.brokerGroupId;
		if (nextBrokerGroupId) setBrokerGroupId(nextBrokerGroupId);
	}, [
		salesTeamUserDetails?.brokerGroupId,
		setBrokerGroupId,
		clientProps?.clientRow?.brokerGroupId,
	]);

	useEffect(() => {
		if (values[CLIENT_ID] && clientProps) {
			const clientInsight = clientProps.insight;

			let city = clientInsight?.City ?? '';
			let postcode = clientInsight?.Postcode ?? '';
			if ((!city || !postcode) && typeof clientInsight?.AddressLine3 === 'string') {
				const addressParts = clientInsight.AddressLine3.split(',');
				if (!city) city = addressParts[0] ?? '';
				if (!postcode) postcode = addressParts[1] ?? '';
			}
			let suburb = clientInsight?.Suburb?.trim();
			if (!suburb) suburb = clientInsight?.AddressLine2?.trim() ?? '';
			if (clientProps?.clientRow?.brokerGroupId)
				setFieldValue(BROKER_GROUP_ID, clientProps?.clientRow?.brokerGroupId);
			setFieldValue(INDUSTRY, clientProps?.clientRow?.industry ?? '');
			setFieldValue(ANZSIC_CODE, clientProps?.clientRow?.standardIndustryCode);
			setFieldValue(ADDRESS, clientInsight?.AddressLine1 ?? '');
			setFieldValue(POSTCODE, postcode?.trim() ?? '');
			setFieldValue(COUNTRY, clientInsight?.CountryName ?? '');
			setFieldValue(SUBURB, suburb?.trim() ?? '');
			setFieldValue(CITY, city?.trim() ?? '');
			setFieldValue(SOURCE_OF_BUSINESS, clientInsight?.SourceOfBusinessId);
			if (clientProps?.clientRow?.annualRevenue)
				setFieldValue(ANNUAL_TURNOVER, clientProps?.clientRow?.annualRevenue ?? 0);
			if (clientProps?.website) setFieldValue(WEBSITE, clientProps?.website ?? '');
			if (clientProps?.clientRow?.description || clientProps?.description)
				setFieldValue(
					OTHER_INFO,
					clientProps?.clientRow?.description ?? clientProps?.description,
				);
		}
	}, [values[CLIENT_ID], clientProps]);

	const submitButtonDisabled = isLoading || isUninitialized || loading || contactQuery.isLoading;

	useEffect(() => {
		setSchema(
			newBusinessValidationSchema(isClassOfRiskRequired, isDealAmountRequired)
				.omit([FOLDER_NAME])
				.concat(folderValidationSchema(values.existingFolders, 'Deal name')),
		);
	}, [values.existingFolders, isClassOfRiskRequired, isDealAmountRequired]);

	useEffect(() => {
		setSalesTeam?.(values[SALESTEAM]);
	}, [values[SALESTEAM]]);

	const gridProps = {
		rowSpacing: 2,
		columnSpacing: 2,
	};

	return (
		<Form>
			<FormNavigationBlocker />
			<FormikErrorScroll isSubmitting={isSubmitting} />
			<Box
				sx={{
					flex: 1,
					overflowY: 'auto',
					paddingBottom: '2em',
				}}
			>
				<Container sx={{ pt: 3 }}>
					<PageHeader
						title='Add New Business'
						icon={getPipelineIcon(
							process.env.REACT_APP_PIPELINE_NEW_BUSINESS,
							'40px',
							theme.palette.mode === 'dark' ? { bgColor: 'transparent' } : undefined,
						)}
					/>
					<CardContent>
						<Typography variant='body1' mb='1.15em'>
							{'Fill out the following form to create your new business deal.'}
						</Typography>
						{isLoading || isUninitialized ? (
							<CircularProgress />
						) : (
							<Stack spacing={gridProps.rowSpacing}>
								<Grid container {...gridProps}>
									<DealNaming dealName='Deal name' />
								</Grid>

								<Typography variant='form_label'>Client details</Typography>
								<Grid container {...gridProps}>
									<SearchClient
										disabled={
											isExistingClient ||
											contactQuery.isLoading ||
											isSubmitting
										}
									/>
									<Grid size={12}>
										<BusinessDescription loading={loading} />
									</Grid>
								</Grid>

								<Typography variant='form_label'>Deal details</Typography>
								<Grid container {...gridProps}>
									<Grid size={12}>
										<AdditionalScopeFields
											loading={loading}
											disabled={isSubmitting || !canCreate}
										/>
									</Grid>

									<Grid size={{ xs: 12, md: 4 }}>
										<RenewalDateField />
									</Grid>

									<Grid size={{ xs: 12, md: 4 }}>
										<AdviserFeeField loading={loading} />
									</Grid>
									<Grid size={12}>
										<FileUpload
											files={values.files}
											setFiles={(files) => {
												setFieldValue(FILES, files);
											}}
											loading={loading}
										/>
										<FormHelperText>{UPLOAD_FILES_HELP_TEXT}</FormHelperText>
									</Grid>
								</Grid>
								<DealFormMessages />
							</Stack>
						)}
					</CardContent>
				</Container>
			</Box>
			<DealFormActions disabled={submitButtonDisabled} />
		</Form>
	);
};
